import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Chip, CircularProgress, Alert, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PoolIcon from '@mui/icons-material/Pool'; // Placeholder icon for amenities
import { RoomService } from '../../api/services/roomService';
import AmenityFormModal from '../../components/AmenityFormModal';
import AddIcon from '@mui/icons-material/Add';
import { extractTotalCount } from '../../components/common/tableStyles';

export default function AmenitySetup() {
    const [amenities, setAmenities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchAmenities = async () => {
        setLoading(true);
        try {
            const response = await RoomService.getAmenities({ page, limit: rowsPerPage });
            
            // Handle various backend response structures
            let dataArray = [];
            if (Array.isArray(response?.data?.data)) dataArray = response.data.data;
            else if (Array.isArray(response?.data?.items)) dataArray = response.data.items;
            else if (Array.isArray(response?.data)) dataArray = response.data;
            else if (Array.isArray(response?.items)) dataArray = response.items;
            else if (Array.isArray(response)) dataArray = response;
            
            setAmenities(dataArray);
            
            const meta = response?.meta || response?.pagination || response?.data?.meta || response?.data?.pagination || response?.data || response || {};
            setTotal(extractTotalCount(response, dataArray));
            setError(null);
        } catch (err) {
            console.error('Failed to fetch amenities:', err);
            setError(err.message || 'Failed to load amenities. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAmenities();
    }, [page, rowsPerPage]);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Property Amenities Setup</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="outlined"
                        startIcon={<RefreshIcon />} 
                        onClick={fetchAmenities} 
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => setModalOpen(true)}
                    >
                        Add Amenity
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <AmenityFormModal 
                open={modalOpen} 
                onClose={() => setModalOpen(false)} 
                onRefresh={fetchAmenities} 
            />

            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }} color="text.secondary">Loading amenities...</Typography>
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Amenity Name</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {amenities.length > 0 ? (
                                amenities.map((amenity) => (
                                    <TableRow key={amenity.id} hover>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PoolIcon fontSize="small" color="primary" />
                                                {amenity.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={amenity.category || 'General'} 
                                                size="small" 
                                                color="secondary" 
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                            {amenity.description || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={amenity.status || 'Active'} 
                                                size="small" 
                                                color={amenity.status === 'Active' ? 'success' : 'default'} 
                                                variant="outlined" 
                                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No amenities defined for this property yet.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={amenities.length === 0 ? 0 : total}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                    />
                </TableContainer>
            )}
        </Box>
    );
}
